import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import Logo from '../components/site/Logo';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session } = useAuth();

  const [checking, setChecking] = useState(true);
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success
  const [error, setError] = useState('');
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    // Supabase attaches the recovery session via the URL hash; onAuthStateChange fires with event PASSWORD_RECOVERY.
    // We just wait a moment for the SDK to hydrate, then check for a session.
    let mounted = true;
    const t = setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      if (data?.session || session) {
        setReady(true);
      } else {
        const err = searchParams.get('error_description') || searchParams.get('error') || 'This reset link is invalid or has expired.';
        setLinkError(err);
      }
      setChecking(false);
    }, 400);
    return () => { mounted = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords don’t match.'); return; }
    setStatus('loading');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setStatus('idle');
      setError(error.message || 'Could not update password.');
      return;
    }
    setStatus('success');
    setTimeout(() => navigate('/projects', { replace: true }), 1200);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
      <header className="px-6 lg:px-10 py-6">
        <Link to="/" style={{ color: 'var(--yb-ink)' }} className="inline-flex"><Logo /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <p className="text-[12.5px] uppercase tracking-widest mb-3" style={{ color: 'var(--yb-muted)' }}>Reset password</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight" style={{ color: 'var(--yb-ink)' }}>
            Set a new <span className="italic">password.</span>
          </h1>

          {checking && (
            <div className="mt-8 flex items-center gap-2 text-[14px]" style={{ color: 'var(--yb-muted)' }}>
              <Loader2 size={15} className="animate-spin" /> Verifying your reset link…
            </div>
          )}

          {!checking && linkError && (
            <div className="mt-6 p-4 rounded-xl border" style={{ borderColor: '#F1D9C4', backgroundColor: '#FDF5EE' }}>
              <p className="text-[13.5px] font-medium" style={{ color: '#7A3B03' }}>{linkError}</p>
              <p className="mt-1 text-[13px]" style={{ color: '#7A3B03' }}>
                Ask for a new reset email on the <Link to="/login" className="underline">login page</Link>.
              </p>
            </div>
          )}

          {!checking && ready && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>New password</label>
                <div className="relative">
                  <input
                    type={show ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full pl-4 pr-11 py-2.5 rounded-lg border text-[15px] outline-none bg-white"
                    style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? 'Hide password' : 'Show password'}
                    aria-pressed={show}
                    className="yb-eye-toggle absolute top-1/2 -translate-y-1/2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors"
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Confirm new password</label>
                <input
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat new password"
                  className="w-full px-4 py-2.5 rounded-lg border text-[15px] outline-none bg-white"
                  style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                />
              </div>
              <button
                type="submit"
                disabled={status !== 'idle'}
                className="yb-btn-primary w-full py-2.5 rounded-lg font-semibold text-[14.5px] flex items-center justify-center gap-1.5 disabled:opacity-70"
              >
                {status === 'loading' && <Loader2 size={15} className="animate-spin" />}
                {status === 'success' && <Check size={15} />}
                {status === 'idle' && 'Update password'}
                {status === 'loading' && 'Updating…'}
                {status === 'success' && 'Password updated'}
              </button>
              {error && (
                <p className="text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}>
                  <AlertCircle size={14} /> {error}
                </p>
              )}
              {status === 'success' && (
                <p className="text-[13px]" style={{ color: 'var(--yb-indigo)' }}>
                  You’re signed in. Taking you to your projects…
                </p>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
